<template>
  <div class="ces-chart-container" ref="container">
    <div v-if="isEmpty" class="empty-state">
      <v-icon size="48" color="grey lighten-2">mdi-chart-line-variant</v-icon>
      <div class="mt-2 grey--text text--lighten-1">
        <slot name="empty">Sin datos para el período seleccionado</slot>
      </div>
    </div>

    <template v-else>
      <div class="chart-wrapper" ref="wrapper">
        <svg
          :viewBox="`0 0 ${W} ${H}`"
          preserveAspectRatio="none"
          width="100%"
          :height="height + 'px'"
          class="ces-svg"
        >
          <!-- Horizontal grid lines -->
          <line
            v-for="tick in yTicks"
            :key="`grid-${tick}`"
            :x1="PAD_L"
            :y1="yPos(tick)"
            :x2="W - PAD_R"
            :y2="yPos(tick)"
            stroke="#f0f0f0"
            stroke-width="1"
          />

          <!-- Y-axis labels -->
          <text
            v-for="tick in yTicks"
            :key="`ylabel-${tick}`"
            :x="PAD_L - 8"
            :y="yPos(tick) + 4"
            text-anchor="end"
            font-size="11"
            font-family="Inter, sans-serif"
            fill="#8593A3"
          >{{ tick }}</text>

          <!-- X-axis labels -->
          <text
            v-for="item in visibleXLabels"
            :key="`xlabel-${item.index}`"
            :x="xPos(item.index)"
            :y="H - 10"
            text-anchor="middle"
            font-size="9"
            font-family="Inter, sans-serif"
            fill="#8593A3"
          >{{ formatPeriod(item.period) }}</text>

          <!-- Left axis line -->
          <line
            :x1="PAD_L"
            :y1="PAD_T"
            :x2="PAD_L"
            :y2="H - PAD_B"
            stroke="#e0e0e0"
            stroke-width="1"
          />
          <!-- Bottom axis line -->
          <line
            :x1="PAD_L"
            :y1="H - PAD_B"
            :x2="W - PAD_R"
            :y2="H - PAD_B"
            stroke="#e0e0e0"
            stroke-width="1"
          />

          <!-- Series polylines -->
          <polyline
            v-for="s in series"
            :key="`line-${s.name}`"
            :points="getSeriesPoints(s)"
            :stroke="s.color"
            stroke-width="2.5"
            fill="none"
            stroke-linejoin="round"
            stroke-linecap="round"
            opacity="0.9"
          />

          <!-- Interactive dots -->
          <g v-for="s in series" :key="`dots-${s.name}`">
            <circle
              v-for="d in s.data"
              :key="`${s.name}-${d.period}`"
              :cx="xPos(periodIndex[d.period])"
              :cy="yPos(d.avgCES)"
              r="5"
              :fill="s.color"
              stroke="white"
              stroke-width="2"
              class="ces-dot"
              @mouseenter="onDotEnter($event, d, s)"
              @mouseleave="onDotLeave"
              @click="onDotEnter($event, d, s)"
            />
          </g>
        </svg>

        <!-- Tooltip -->
        <transition name="ces-fade">
          <div
            v-if="tooltip.visible"
            class="ces-tooltip"
            :style="tooltipStyle"
          >
            <div class="ces-tooltip__date">{{ tooltip.period }}</div>
            <div class="ces-tooltip__series">
              <span class="ces-tooltip__dot" :style="{ background: tooltip.color }"></span>
              <span class="ces-tooltip__name">{{ tooltip.seriesName }}</span>
            </div>
            <div class="ces-tooltip__values">
              <div>CES: <b>{{ tooltip.avgCES }}</b></div>
              <div>Respuestas: <b>{{ tooltip.count }}</b></div>
            </div>
          </div>
        </transition>
      </div>

      <!-- Legend -->
      <div class="ces-legend mt-2">
        <span v-for="s in series" :key="`legend-${s.name}`" class="ces-legend__item">
          <span class="ces-legend__dot" :style="{ background: s.color }"></span>
          {{ s.name }}
        </span>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'CESLineChart',
  props: {
    /** Array of series: [{ name, color, data: [{ period, avgCES, count }] }] */
    series: {
      type: Array,
      default: () => [],
    },
    height: {
      type: Number,
      default: 260,
    },
  },
  data() {
    return {
      W: 800,
      H: 250,
      PAD_L: 48,
      PAD_R: 20,
      PAD_T: 16,
      PAD_B: 44,
      yTicks: [1, 2, 3, 4, 5],
      tooltip: {
        visible: false,
        x: 0,
        y: 0,
        period: '',
        avgCES: null,
        count: null,
        color: '',
        seriesName: '',
      },
    };
  },
  computed: {
    isEmpty() {
      return this.series.length === 0 || this.series.every(s => !s.data || s.data.length === 0);
    },
    allPeriods() {
      const set = new Set();
      for (const s of this.series) {
        for (const d of s.data || []) set.add(d.period);
      }
      return [...set].sort();
    },
    periodIndex() {
      const map = {};
      this.allPeriods.forEach((p, i) => { map[p] = i; });
      return map;
    },
    chartW() {
      return this.W - this.PAD_L - this.PAD_R;
    },
    chartH() {
      return this.H - this.PAD_T - this.PAD_B;
    },
    xStep() {
      const n = this.allPeriods.length;
      if (n <= 8) return 1;
      if (n <= 20) return 2;
      if (n <= 40) return 4;
      return Math.ceil(n / 10);
    },
    visibleXLabels() {
      const last = this.allPeriods.length - 1;
      return this.allPeriods
        .map((p, i) => ({ period: p, index: i }))
        .filter(({ index: i }) => i % this.xStep === 0 || i === last);
    },
    tooltipStyle() {
      return {
        left: `${this.tooltip.x}px`,
        top: `${this.tooltip.y}px`,
      };
    },
  },
  methods: {
    xPos(index) {
      const n = this.allPeriods.length;
      if (n <= 1) return this.PAD_L + this.chartW / 2;
      return this.PAD_L + (index / (n - 1)) * this.chartW;
    },
    yPos(value) {
      const clamped = Math.min(5, Math.max(1, value));
      return this.PAD_T + (1 - (clamped - 1) / 4) * this.chartH;
    },
    getSeriesPoints(s) {
      if (!s.data || s.data.length === 0) return '';
      return s.data
        .map(d => {
          const xi = this.periodIndex[d.period];
          return `${this.xPos(xi)},${this.yPos(d.avgCES)}`;
        })
        .join(' ');
    },
    formatPeriod(p) {
      if (!p) return '';
      const date = new Date(p + 'T00:00:00');
      const months = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
      ];
      return `${months[date.getMonth()]} ${date.getFullYear()}`;
    },
    onDotEnter(event, data, s) {
      const wrapper = this.$refs.wrapper;
      if (!wrapper) return;
      const wrapperRect = wrapper.getBoundingClientRect();
      const circleRect = event.target.getBoundingClientRect();
      const x = circleRect.left - wrapperRect.left + circleRect.width / 2;
      const y = circleRect.top - wrapperRect.top - 10;
      this.tooltip = {
        visible: true,
        x,
        y: Math.max(0, y - 90),
        period: this.formatPeriod(data.period),
        avgCES: data.avgCES,
        count: data.count,
        color: s.color,
        seriesName: s.name,
      };
    },
    onDotLeave() {
      this.tooltip.visible = false;
    },
  },
};
</script>

<style scoped>
.ces-chart-container {
  width: 100%;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: 24px;
}

.chart-wrapper {
  position: relative;
  user-select: none;
}

.ces-svg {
  display: block;
}

.ces-dot {
  cursor: pointer;
  transition: r 0.1s ease;
}

.ces-dot:hover {
  r: 7;
}

/* Tooltip */
.ces-tooltip {
  position: absolute;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px 14px;
  pointer-events: none;
  z-index: 200;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  min-width: 150px;
  transform: translateX(-50%);
}

.ces-tooltip__date {
  font-size: 11px;
  color: #8593A3;
  margin-bottom: 6px;
}

.ces-tooltip__series {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.ces-tooltip__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.ces-tooltip__name {
  font-size: 12px;
  font-weight: 600;
  color: #1B1D21;
}

.ces-tooltip__values {
  font-size: 13px;
  color: #1B1D21;
  line-height: 1.6;
}

/* Legend */
.ces-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
}

.ces-legend__item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #555;
  font-family: Inter, sans-serif;
}

.ces-legend__dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* Tooltip transition */
.ces-fade-enter-active,
.ces-fade-leave-active {
  transition: opacity 0.15s ease;
}
.ces-fade-enter,
.ces-fade-leave-to {
  opacity: 0;
}
</style>
